<template>
  <div class="app-tabs" :class="tabsClasses">
    <div class="app-tabs-navigation">
      <button
        class="app-tabs-navigation-item"
        v-for="({ label, props, disabled, data, events }, index) in AppTabs.items"
        :key="index"
        :class="{ 'is-active': index === currentTab }"
        :disabled="disabled"
        v-bind="props"
        v-on="events"
        tabindex="0"
        @click="setActiveTab(index)">
        <slot name="app-tab" :tab="{ label, data }" v-if="$scopedSlots['app-tab']"></slot>
        <span class="app-tab-label" v-else>{{ label }}</span>
      </button>

      <span class="app-tabs-indicator" :style="indicatorStyles" :class="indicatorClass" ref="indicator"></span>
    </div>

    <div class="app-tabs-content" :style="contentStyles" v-show="hasContent">
      <div class="app-tabs-container" ref="container" :style="containerStyles">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
  import ObserveElement from 'core/utils/ObserveElement'

  export default {
    name: 'AppTabs',
    props: {
      dense: Boolean,
      dynamicHeight: Boolean,
      activeTab: [String, Number]
    },
    data: () => ({
      resizeObserver: null,
      elementObserver: null,
      currentTab: 0,
      currentTabIndex: 0,
      indicatorStyles: {},
      indicatorClass: null,
      noTransition: true,
      containerStyles: {},
      contentStyles: {
        height: '0px'
      },
      hasContent: false,
      AppTabs: {
        items: {}
      }
    }),
    provide () {
      return {
        AppTabs: this.AppTabs
      }
    },
    computed: {
      tabsClasses () {
        return {
          'app-tabs-no-transition': this.noTransition,
          'app-tabs-dense': this.dense,
          'app-tabs-dynamic-height': this.dynamicHeight
        }
      }
    },
    watch: {
      AppTabs: {
        deep: true,
        handler () {
          this.setHasContent()
        }
      },
      async currentTab () {
        await this.$nextTick()
        this.setIndicatorStyles()
        this.setActiveTabIndex()
        this.calculateTabPos()
      },
      activeTab (tab) {
        this.currentTab = tab
        this.$emit('changed', tab)
      }
    },
    methods: {
      hasActiveTab () {
        return this.currentTab || this.activeTab
      },
      getItemsAndKeys () {
        const items = this.AppTabs.items

        return {
          items,
          keys: Object.keys(items)
        }
      },
      setActiveTab (index) {
        this.currentTab = index
        this.$emit('changed', index)
      },
      setActiveTabIndex () {
        const activeButton = this.$el.querySelector('.app-tabs-navigation-item.is-active')

        if (activeButton) {
          this.currentTabIndex = [].indexOf.call(activeButton.parentNode.childNodes, activeButton)
        }
      },
      setActiveTabByIndex (index) {
        const { keys } = this.getItemsAndKeys()

        if (!this.hasActiveTab()) {
          this.currentTab = keys[index]
        }
      },
      setHasContent () {
        const { items, keys } = this.getItemsAndKeys()

        this.hasContent = keys.some(key => items[key].hasContent)
      },
      setIndicatorStyles () {
        window.requestAnimationFrame(async () => {
          await this.$nextTick()

          const activeButton = this.$el.querySelector('.app-tabs-navigation-item.is-active')

          if (activeButton && this.$refs.indicator) {
            const buttonWidth = activeButton.offsetWidth
            const buttonLeft = activeButton.offsetLeft
            const indicatorLeft = this.$refs.indicator.offsetLeft

            if (indicatorLeft < buttonLeft) {
              this.indicatorClass = 'app-tabs-indicator-right'
            } else {
              this.indicatorClass = 'app-tabs-indicator-left'
            }

            this.indicatorStyles = {
              left: `${buttonLeft}px`,
              right: `calc(100% - ${buttonWidth + buttonLeft}px)`
            }
          }
        })
      },
      calculateTabPos () {
        if (this.hasContent) {
          const tabElement = this.$el.querySelector(`.app-tab:nth-child(${this.currentTabIndex + 1})`)

          this.contentStyles = {
            height: `${tabElement.offsetHeight}px`
          }

          this.containerStyles = {
            transform: `translate3D(${-this.currentTabIndex * 100}%, 0, 0)`
          }
        }
      },
      setupObservers () {
        if ('ResizeObserver' in window) {
          this.resizeObserver = new window.ResizeObserver(this.setIndicatorStyles)
          this.resizeObserver.observe(this.$el)
        }

        this.elementObserver = ObserveElement(this.$refs.container, {
          childList: true,
          characterData: true,
          subtree: true
        }, () => {
          this.setIndicatorStyles()
          this.calculateTabPos()
        })
      }
    },
    created () {
      this.setHasContent()
      this.currentTab = this.activeTab
    },
    mounted () {
      this.setActiveTabByIndex(0)

      window.setTimeout(() => {
        this.setActiveTabIndex()
        this.calculateTabPos()
        this.noTransition = false
        this.setupObservers()
      }, 100)
    },
    beforeDestroy () {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }

      if (this.elementObserver) {
        this.elementObserver.destroy()
      }

      window.removeEventListener('resize', this.setIndicatorStyles)
    }
  }
</script>

<style lang="scss">
  @import '~stylesheets/meta';

  .app-tabs {
    display: flex;
    flex-direction: column;
    transition: .3s $transition-default;

    &.app-tabs-no-transition * {
      transition: none !important;
    }

    &.app-tabs-dynamic-height .app-tabs-content {
      transition: height .3s $transition-default;
      will-change: height;
    }

    &.app-tabs-dense {
      .app-tabs-navigation-item {
        padding: 16px 6px;
        font-size: 14px;
      }

      .app-tabs-indicator {
        height: 2px;
      }
    }
  }

  .app-tabs-navigation {
    min-height: 49px;
    display: flex;
    justify-content: flex-start;
    position: relative;
    border-bottom: 1px solid $color-light-grey;

    .app-tabs-navigation-item {
      @include clear-button;
      max-width: 264px;
      margin: 0 10px;
      padding: 20px 8px;
      color: $color-dark-grey;
      font-size: 16px;

      &:focus-visible {
        outline: -webkit-focus-ring-color auto 1px;
      }
    }

    .is-active {
      color: $color-tundora;
    }
  }

  .app-tabs-indicator {
    height: 3px;
    position: absolute;
    bottom: 0;
    left: 0;
    transform: translateZ(0);
    will-change: left, right;
    background-color: $color-tundora;

    &.app-tabs-indicator-left {
      transition: left .35s $transition-default,
                  right .4s $transition-default;
    }

    &.app-tabs-indicator-right {
      transition: right .35s $transition-default,
                  left .4s $transition-default;
    }
  }

  .app-tabs-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    transition: none;
    will-change: height;
  }

  .app-tabs-container {
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
    flex: 1;
    transform: translateZ(0);
    transition: transform .35s $transition-ease-out;
    will-change: transform;
  }

  .app-tab-label {
    cursor: pointer;
  }
</style>
